/**
 * Ported from frontend/shared/src/commonMain/kotlin/sk/myslikrok/shared/Models/.
 * A change to the Kotlin changes this file in the same commit — client code manifest §2a.
 *
 * Field for field, name for name, including the optionals. Anything that differs here is a decoding bug
 * waiting to happen on exactly one of the three clients, which is the worst place for it to happen.
 */

export type TokenPair = {
  tokenType: string;
  accessToken: string;
  refreshToken: string;
  expiresInSeconds: number;
};

export type AccountProfile = {
  id: string;
  email: string;
  displayName?: string | null;
  role: string;
  locale: string;
  linkedProviders?: string[];
  /** Whether the address has been proved. Nothing is withheld while false — the banner asks (R0066). */
  emailVerified?: boolean;
};

export type Authentication = {
  account: AccountProfile;
  tokens: TokenPair;
};

export type Child = {
  id: string;
  nickname: string;
  birthYear: number;
  schoolGrade: number;
  avatarCode: string;
  inCalibration: boolean;
  calibrationEndsAt: string;
  archivedAt?: string | null;
};

export type ChildDraft = {
  nickname: string;
  birthYear: number;
  schoolGrade: number;
  avatarCode?: string | null;
};

export type ChildEdit = {
  nickname?: string | null;
  schoolGrade?: number | null;
  avatarCode?: string | null;
};

export type ChallengeGrid = {
  columns: number;
  rows: number;
  cells?: string[];
};

export type ChallengeStimulus = {
  /** What a screen reader announces for the stimulus — composed by the server (R0117). */
  accessibleName?: string;
  kind: string;
  symbols?: string[];
  text?: string | null;
  /** A heading above `text` — a story title. Its own field so a client can style it (R0094). */
  title?: string | null;
  grid?: ChallengeGrid | null;
  exposureMs?: number | null;
};

/** True when the stimulus must be hidden after `exposureMs` — the memorise-then-recall kinds. */
export const isTimed = (stimulus: ChallengeStimulus): boolean =>
  stimulus.exposureMs !== null && stimulus.exposureMs !== undefined && stimulus.exposureMs > 0;

/**
 * The beat between the pattern going and the options arriving (R0092).
 *
 * Without it the options are on screen while the pattern still is, and there is nothing to remember — the
 * child reads the answer off the stimulus. With it they are asked what they saw, which is the task.
 *
 * Two and a half seconds. Long enough that the pattern is gone rather than fading, short enough that a
 * child does not think the app has stopped. **Nothing counts it down**: BR-12 forbids showing a child a
 * clock, so the wait is quiet.
 */
export const OPTIONS_REVEAL_DELAY_MS = 2_500;

/** When the options may appear, measured from the task opening. Zero for anything not memorised. */
export const optionsRevealAtMs = (stimulus: ChallengeStimulus): number =>
  isTimed(stimulus) ? (stimulus.exposureMs ?? 0) + OPTIONS_REVEAL_DELAY_MS : 0;

export const cellAt = (grid: ChallengeGrid, row: number, column: number): string =>
  grid.cells?.[row * grid.columns + column] ?? '';

export type ChallengeOption = {
  /** What a screen reader announces — composed by the server (R0117). */
  accessibleName?: string;
  key: string;
  label: string;
  symbol?: string | null;
};

export type SessionItem = {
  instanceToken: string;
  orderIndex: number;
  /**
   * Which planned slot this task belongs to. A replacement carries the slot of the task it replaces,
   * so the rail holds still rather than growing from nine to ten (R0091).
   */
  slotIndex: number;
  challengeCode: string;
  challengeName: string;
  skillCode: string;
  subskillCode: string;
  level: number;
  presentation: string;
  /** Shown while the stimulus is up; `instruction` takes over once it is gone. Null for a one-moment task. */
  memoriseInstruction: string | null;
  instruction: string;
  stimulus: ChallengeStimulus;
  options?: ChallengeOption[];
  status: string;
  attemptsUsed: number;
  attemptsRemaining: number;
  expiresAt: string;
};

export type TrainingSession = {
  sessionId: string;
  trainingDate: string;
  status: string;
  isExtra: boolean;
  inCalibration: boolean;
  plannedInstanceCount: number;
  completedInstanceCount: number;
  message: string;
  items?: SessionItem[];
};

export type ChallengeAnswer = {
  instanceToken: string;
  optionKeys?: string[] | null;
  text?: string | null;
  ordering?: string[] | null;
  clientReportedTimeMs?: number | null;
};

export type AnswerOutcome = {
  isCorrect: boolean;
  status: string;
  message: string;
  hint?: string | null;
  explanation?: string | null;
  errorType?: string | null;
  attemptsRemaining: number;
  responseTimeMs: number;
  levelChanged: boolean;
  newLevel?: number | null;
  replacementItem?: SessionItem | null;
  sessionCompleted: boolean;
  sessionMessage?: string | null;
  rewardMessage?: string | null;
  starsEarned: number;
  coinsEarned: number;
  /**
   * What finishing the session earned, if anything. Empty is the common case and is not a failure — an
   * achievement is granted when a threshold is crossed, and most sessions cross none.
   */
  earnedAchievements?: EarnedAchievement[];
};

/**
 * An achievement earned by the session that just finished.
 *
 * Carries its rendered strings rather than a key: the API negotiated the language for this request, and a
 * client composing the sentence itself is exactly what BR-13 forbids.
 */
export type EarnedAchievement = {
  code: string;
  name: string;
  description: string;
  symbol: string;
};

/**
 * One achievement as a child sees it: earned or not, and how far along if not.
 *
 * `current` and `threshold` exist so the client can show "5 z 7" rather than a bare lock. A goal you can
 * see the distance to is a goal; a locked box is a refusal.
 */
export type AchievementProgress = {
  code: string;
  family: string;
  tier: number;
  name: string;
  description: string;
  symbol: string;
  skillCode?: string | null;
  isEarned: boolean;
  earnedAt?: string | null;
  current: number;
  threshold: number;
};

/**
 * Everything a child has earned and how far they are from what they have not.
 *
 * `nextUp` is the handful closest to being earned, across every family. It exists because a list of
 * forty-five things is a wall — the child needs the two or three that are within reach.
 *
 * `currentStreakDays` and `bestStreakDays` are both present on purpose, and the **best** one is the number
 * to show. A broken streak costs the current figure and never the best, which is what keeps a streak from
 * punishing a child for being ill (09-Rewards-And-Island §6).
 */
export type AchievementOverview = {
  earnedCount: number;
  totalCount: number;
  currentStreakDays: number;
  bestStreakDays: number;
  achievements?: AchievementProgress[];
  nextUp?: AchievementProgress[];
};

export type IslandPart = {
  code: string;
  name: string;
  costCoins: number;
  isUnlocked: boolean;
  isAffordable: boolean;
  isAvailable: boolean;
};

export type Island = {
  coinBalance: number;
  starBalance: number;
  /** Coins a completed training is guaranteed to pay. Sent by the server, not assumed by the client. */
  coinsPerCompletedSession?: number;
  parts?: IslandPart[];
};

export type Hint = {
  message: string;
  hint: string | null;
  hintsUsed: number;
  hintsRemaining: number;
};

export type Today = {
  status: 'none' | 'in_progress' | 'completed';
  completedCount: number;
  plannedCount: number;
};

export type SkillSummary = {
  skillCode: string;
  skillName: string;
  level: number;
  maximumLevel: number;
  accuracy: number;
  trend: string;
  trendLabel: string;
  closedInstanceCount: number;
};

export type Recommendation = {
  kind: string;
  text: string;
};

export type ChildDashboard = {
  childId: string;
  nickname: string;
  trainingDayCount: number;
  totalTrainingTimeMs: number;
  totalAttemptCount: number;
  inCalibration: boolean;
  skills?: SkillSummary[];
  recommendations?: Recommendation[];
};

export type SubskillDetail = {
  subskillCode: string;
  /** The subskill's name, for people. Nothing user-facing renders the code (R0108). */
  subskillName: string;
  level: number;
  accuracy: number;
  trend: string;
  closedInstanceCount: number;
  averageResponseTimeMs: number;
  lastTrainedAt?: string | null;
};

export type SkillDetail = {
  skillCode: string;
  skillName: string;
  level: number;
  subskills?: SubskillDetail[];
};

export type ErrorPattern = {
  errorType: string;
  occurrences: number;
  share: number;
  description: string;
};

export type Registration = {
  email: string;
  password: string;
  displayName?: string | null;
  locale?: string | null;
};

export type FamilyOverviewAccount = {
  email: string;
  emailVerified: boolean;
  locale: string;
};

export type FamilyOverviewChild = {
  childId: string;
  nickname: string;
  trainingDayCount: number;
  totalTrainingTimeMs: number;
  todayStatus: 'none' | 'in_progress' | 'completed';
  inCalibration: boolean;
};

export type FamilyOverviewTotals = {
  childCount: number;
  trainingDayCount: number;
  totalTrainingTimeMs: number;
};

/** `GET /insights/overview` — the family at a glance. Numbers only; every word is the client's (R0087). */
export type FamilyOverview = {
  account: FamilyOverviewAccount;
  children: FamilyOverviewChild[];
  totals: FamilyOverviewTotals;
};

/** The sign-in providers the API can be configured with. Facebook and Google are what the buttons offer. */
export type OauthProvider = 'google' | 'facebook';
